import type {
  ArtPack,
  ArtPackManifest,
  AuthoredDirectionalAnimTable,
  SpriteTemplate,
} from "../../art/types";
import {
  loadAuthoredDirectionalAnimSet,
  singleFrameAnimSet,
} from "../../art/anim-set";
import affiliation from "../tables/art/packs/infinite-war/affiliation.json";
import packJson from "../tables/art/packs/infinite-war/pack.json";
import palette from "../tables/art/packs/infinite-war/palette.json";

const spriteMods = import.meta.glob(
  "../tables/art/packs/infinite-war/sprites/*.json",
  { eager: true, import: "default" },
) as Record<string, SpriteTemplate>;

const animMods = import.meta.glob(
  "../tables/art/packs/infinite-war/anims/*.json",
  { eager: true, import: "default" },
) as Record<string, AuthoredDirectionalAnimTable>;

function idFromPath(p: string): string {
  const base = p.split("/").pop() ?? p;
  return base.replace(/\.json$/, "");
}

/** Infinite War dedicated combat kits + attack FX + dominion tiles. */
export function loadInfiniteWarArtPack(): ArtPack {
  const sprites: Record<string, SpriteTemplate> = {};
  const anims: ArtPack["anims"] = {};

  for (const [path, table] of Object.entries(animMods)) {
    const id = idFromPath(path);
    const authored = table as AuthoredDirectionalAnimTable;
    const set = loadAuthoredDirectionalAnimSet(id, authored);
    anims[id] = set;
    const idle0 = authored.dirs?.e?.idle?.frames?.[0];
    if (idle0) {
      sprites[id] = { id, size: 64, rows: idle0 };
    }
  }

  for (const [path, sprite] of Object.entries(spriteMods)) {
    const id = idFromPath(path);
    if (!sprites[id]) sprites[id] = sprite;
    if (!anims[id]) {
      anims[id] = singleFrameAnimSet(sprites[id]!, 2);
    }
  }

  return {
    manifest: packJson as ArtPackManifest,
    palette,
    affiliation,
    sprites,
    anims,
  };
}
